import ProviderInvoiceMessageFormConfig from "./form";
import GenericForm from "../GenericForm";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { useRef, useState } from "react";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import LoadXmlDataForm from "@components/dynamic/LoadXmlDataForm";
import { generateATVDocumentKey, generateATVDocumentSequence, generateATVDocumentSequenceNumber, InformacionReferencia, loadXmlDocument, XmlSummaryData } from "@utils/EDocument";
import LoadingScreen from "@components/content/LoadingScreen";
import { ProviderInvoiceMessageFormData } from "@/formTypes/ProviderInvoiceMessageFormDataType";
import { Button, Card, CardText, Col, Label, Row } from "reactstrap";
import { ATV_RESPONSES_CODE_TO_EDOCUMET_TYPE, SITUACION_COMPROBANTE_NORMAL } from "@utils/Constants";
import { getCurrentDateTime } from "@utils/Api";
import { useAppSelector } from "@/store/hooks";
import { ApiError, NotFoundError } from "@/api/ApiClient";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { MessageMetadata } from "@/atv/eInvoiceApi";
import { DocumentSequenceApi } from "@/api/DocumentSequenceApi";
import { AtvMessageApi, AtvMessageDataType } from "@/api/AtvMessageApi";
import { showAlert } from "@utils/Alert";
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { findPropertyRecursive, omitProperties } from "@utils/Utils";
import { ProviderNoteApi, ProviderNoteDataType } from "@/api/ProviderNoteApi";
import { ReceivedInvoiceMessageData } from "@/formTypes/ReceivedInvoiceDataType";
import { markRecieveInvoiceAsValidated } from "@utils/RecieveEmailUtils";

type ProviderInvoiceNoteMessageFormProps = {
  formId: string,
  saveRedirectUrl: string
}

const ProviderInvoiceNoteMessageForm = (props: ProviderInvoiceNoteMessageFormProps) => {
  const formRef = useRef<DynamicFormRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceLoaded, setInvoiceLoaded] = useState<boolean>(false);
  const [providerInvoiceData, setProviderInvoiceData] = useState<ProviderInvoiceDataType>();
  const [providerInvoiceNoteData, setProviderInvoiceNoteData] = useState<ProviderNoteDataType>();
  const [documentSummary, setDocumentSummary] = useState<XmlSummaryData>({} as XmlSummaryData);
  const suscriptionData = useAppSelector(state => state.suscription.suscription);
  const [receivedEmailId, setReceivedEmailId] = useState<string>();

  const onXMLFileLoad = async (fileData: any, receivedEmailId?: string) => {
    setIsLoading(true)
    try {
      if (receivedEmailId) {
        setReceivedEmailId(receivedEmailId);
      }
      const { messageData, providerInvoiceData, documentSummary } = await loadXmlDocument(fileData, ["NotaCreditoElectronica", "NotaDebitoElectronica"])
      const referencias = findPropertyRecursive(fileData, "InformacionReferencia")
      let referencia;
      if (Array.isArray(referencias)) {
        referencia = referencias[0]
      } else {
        referencia = referencias
      }

      if (!referencia) {
        showAlert("warning", `La nota el documento seleccionado no tiene ninguna informacion de referencia que se
           pueda utilizar para asociar esta nota de credito/ debito a una factura dentro de las cuentas por pagar. 
           Por favor ingrese la nota de Credito/Debito manualmente despues de aceptar esta Nota`)
      }

      await loadProviderInvoice(referencia);
      setProviderInvoiceNoteData(providerInvoiceData)
      setInvoiceLoaded(true)
      setDocumentSummary(documentSummary as XmlSummaryData)

      const defaultActivityCodeCurrency = await ActivityCodeApi.getDefaultActivityCode()
      const tipoNota = documentSummary?.documentType == "NotaCreditoElectronica" ? 0 : 1;

      formRef.current?.resetFormValue({
        ...messageData,
        tipo: tipoNota,
        activitycode: messageData?.activitycode || defaultActivityCodeCurrency?.code
      });
    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", err.message)
      }
    }
    finally {
      setIsLoading(false)
    }
  }

  const loadProviderInvoice = async (reference: InformacionReferencia) => {
    try {
      const providerInvoice = await ProviderInvoiceApi.getInvoiceByDocumentKey(reference?.Numero)
      setProviderInvoiceData(providerInvoice)
    } catch (err) {
      if (err instanceof NotFoundError) {
        showAlert("warning", `La factura a la que hace referencia esta nota de Credito/Debito no se encuentra registrada en el sistema.
           Por favor, si es necesario, ingresela antes de aceptar esta nota de Credito/Debito`)
      } else {
        throw err
      }
      setProviderInvoiceData({})
    }
  }

  const saveProviderInvoiceNote = async (providerNoteData: ProviderNoteDataType): Promise<ProviderNoteDataType> => {
    let providerNote;

    try {
      providerNote = await ProviderNoteApi.getProviderNoteByDocumentKey(`${providerNoteData.document_key}`);
    } catch (err) {
      if (err instanceof NotFoundError) {
        //Provider note invoices not found create it
        providerNote = await ProviderNoteApi.create(providerNoteData);
      } else {
        throw err
      }
    }
    return providerNote
  }

  const validateInvoice = async (data: ProviderInvoiceMessageFormData): Promise<ProviderInvoiceMessageFormData> => {
    let isExisteProvider = false
    if (suscriptionData && providerInvoiceNoteData) {
      const sequence = await generateATVDocumentSequence(ATV_RESPONSES_CODE_TO_EDOCUMET_TYPE[`${data.mensaje}`])
      const sequenceNumber = generateATVDocumentSequenceNumber(sequence);
      const documentKey = generateATVDocumentKey({
        fecha: getCurrentDateTime(),
        sequence: sequenceNumber
      }, suscriptionData, SITUACION_COMPROBANTE_NORMAL, "fecha")

      let providerInvoiceNote;
      if (data.providerId) {
        isExisteProvider = true;
        const noteData = omitProperties(providerInvoiceNoteData, ["pagada", "providerId"], true);
        providerInvoiceNote = await saveProviderInvoiceNote({
          ...noteData,
          providerInvoiceId: providerInvoiceData?.id,
          tipo: `${data.tipo}`
        });
      }

      const atvMessage: AtvMessageDataType = {
        documentKey: documentKey,
        sequence: `${sequenceNumber}`,
        numeroCedulaEmisor: `${documentSummary.emisor?.Identificacion?.Numero}`,
        fechaEmisionDoc: `${documentSummary.fechaEmision}`,
        mensaje: `${data.mensaje}`,
        detalleMensaje: data.detalle || "",
        montoTotalImpuesto: Number(data.impuestoAcreditar || 0),
        codigoActividad: `${data.activitycode}`,
        condicionImpuesto: `${data.condicionImpuesto}`,
        montoTotalImpuestoAcreditar: Number(data.impuestoAcreditar || 0),
        montoTotalDeGastoAplicable: Number(data.gastoAplicable || 0),
        totalFactura: Number(documentSummary.totalComprobanteAmount || 0),
        providerInvoiceId: providerInvoiceData?.id,
        providerId: data.providerId || "none",
        providerNoteId: providerInvoiceNote?.id
      }

      const savedMessage = await AtvMessageApi.create(atvMessage);

      const messageBody: Partial<MessageMetadata> = {
        ...atvMessage,
        id: savedMessage.id,
        clave: `${providerInvoiceNoteData.document_key}`,
        numeroCedulaReceptor: `${documentSummary.receptor?.Identificacion?.Numero}`,
        numeroConsecutivoReceptor: sequenceNumber,
        tipo_identificacion: `${documentSummary.tipo_identificacion}`,
        identificacion: `${documentSummary.identificacion}`.padStart(2, '0'),
        metadata: {
          calltime: Date.now(),
          numeroConsecutivo: sequenceNumber,
          documentType: "Message",
          clave: documentKey,
          fechaEmisionUTC: `${documentSummary.fechaEmision}`
        }
      }

      const easyposEInvoice = new EasyposEInvoice(suscriptionData)
      await easyposEInvoice.submitMessage(messageBody);
      await DocumentSequenceApi.save(sequence)
      if (!isExisteProvider) {
        showAlert("warning", `El mensaje a Trinutacion fue enviado correctamente, pero el proveedor de este documento no se encuentra 
          registrado en el sistema, por lo que la factura no fue agregada a cuentas por pagar. Por favor agregue la factura manualmente`, {}, 7000)
      }
      if (providerInvoiceNote) {
        showAlert("success", `El mensaje se ha enviado exitosamente a Tributacion y la nota de credito/debito #${providerInvoiceNote.sequence} ha sido agrega a las cuentas por pagar`, {}, 7000)
        if (receivedEmailId) {
          const documentData: Partial<ReceivedInvoiceMessageData> = {
            documentId: providerInvoiceNote.id,
            documentType: "providerInvoice",
            providerId: providerInvoiceData?.providerId,
            reference_number: providerInvoiceData?.reference_number,
            documentNoteType: providerInvoiceData?.tipo == "1" ? "debit" : "credit"
          }
          setReceivedEmailId("")
          await markRecieveInvoiceAsValidated(receivedEmailId, messageBody, documentData)
        }
      }

      return data
    }
    throw new ApiError("No fue posible generar el consecutivo del Mensaje", "API_ERROR");
  }

  return (
    <>
      <LoadXmlDataForm
        paramLoadable={true}
        onFileLoaded={onXMLFileLoad}
      />
      <GenericForm<ProviderInvoiceMessageFormData, ProviderInvoiceMessageFormData>
        ref={formRef}
        formId={props.formId}
        saveRedirectUrl={props.saveRedirectUrl}
        formConfig={ProviderInvoiceMessageFormConfig}
        createFn={(...arg) => validateInvoice(...arg)}
        successMessage="La factura fue validada correctamente"
        errorMessage="No fue posible validar la factura correctamente"
      />
      {invoiceLoaded && (
        <>
          <Row className="g-1 mb-4">
            <Col md={{
              offset: 1,
              size: 9
            }}>
              <Card
                className="mt-3"
                body
                color="light"
              >
                <CardText>
                  <h3 className="text-center mb-3 mt-3">Emisor: {documentSummary.providerName}</h3>
                  <Row>
                    <Col md={{
                      offset: 2,
                      size: 3
                    }}>
                      <b >
                        Numero Factura:
                      </b>
                    </Col>
                    <Col md>
                      <Label>
                        {documentSummary.documentKey}
                      </Label>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={{
                      offset: 2,
                      size: 3
                    }}>
                      <b >
                        Total Impuestos:
                      </b>
                    </Col>
                    <Col md>
                      <Label>
                        {documentSummary.totalImpuesto}
                      </Label>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={{
                      offset: 2,
                      size: 3
                    }}>
                      <b >
                        Total Factura:
                      </b>
                    </Col>
                    <Col md>
                      <Label>
                        {documentSummary.totalComprobante}
                      </Label>
                    </Col>
                  </Row>
                </CardText>
              </Card>
            </Col >
          </Row >
          <div className="d-flex justify-content-center">
            <Button color="primary" size="lg" type="submit" form={props.formId}>
              Enviar Mensaje
            </Button>
          </div>
        </>
      )}

      <LoadingScreen isLoading={isLoading} />
    </>
  )
}

export default ProviderInvoiceNoteMessageForm;